import { Box } from '@mui/material'
import Typography from '@mui/material/Typography'
import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { APIError } from 'components/core/errors/api'
import { LoginButton, LoginCard, LoginLayout, LoginTitle } from 'components/core/login-helper'
import { CdnWarning } from 'components/no-ui/context/auth/CdnWarning'
import { AuthStyles } from 'components/no-ui/context/auth/styles'
import { PrivacyAndTerms } from 'components/pages/login/PrivacyAndTerms'
import { expiredSession, loadSession } from 'lib/actions/session'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { AuthContext, AuthContextRecord } from 'lib/context/app'
import { createTransport, handleLogin, loginPath } from 'lib/oauth'
import { initialSessionState, sessionReducer } from 'lib/reducers/session'
import { Status } from 'lib/rpc'
import { InstallationDomain, TenantDomain } from 'lib/tenant'
import { useRouter } from 'next/router'
import { ReactNode, useCallback, useContext, useEffect, useReducer, useState } from 'react'
import { FormattedMessage } from 'react-intl'

const AuthContextProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
    const router = useRouter()
    const [authContext, setAuthContext] = useState<AuthContextRecord>(useContext(AuthContext))
    const [sessionState, dispatch] = useReducer(sessionReducer, initialSessionState)

    const sessionClient = sessionState.get('client')
    const session = sessionState.get('item')

    // We want to memoize our oauth client and grpc interceptor so they are stable for our children.
    const [authError, setAuthError] = useState<Error | null>(null)
    const [transport, setTransport] = useState<GrpcWebFetchTransport | null>(null)
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false)

    const handleAuthError = useCallback(
        (err: Error) => {
            setAuthError((prevErr) => {
                if (prevErr !== null) {
                    return prevErr
                }
                return err
            })
        },
        [setAuthError],
    )

    useEffect(() => {
        if (!sessionClient) {
            return
        }
        setTransport(createTransport(sessionClient, handleAuthError))
    }, [sessionClient, setTransport, handleAuthError])

    useEffect((): CancelFunc => {
        if (!router.isReady) {
            return emptyCancel
        }
        return loadSession(dispatch, router)
    }, [router.isReady]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect((): CancelFunc => {
        if (!router.isReady || !authError || !sessionClient) {
            return emptyCancel
        }
        return expiredSession(dispatch, router, sessionClient)
    }, [authError, router, sessionClient, dispatch])

    useEffect(() => {
        setAuthContext(authContext.set('oauth', sessionClient).set('transport', transport))
    }, [authContext, setAuthContext, sessionClient, transport])

    useEffect(() => {
        if (!router.isReady || !sessionClient) {
            return
        }
        if (router.pathname !== loginPath) {
            return
        }
        switch (router.query.sso_operation) {
            case 'initiate_login':
                setButtonDisabled(true)
                // This switch case is only entered once sessionClient is set on loadSession
                // loadSession tries to get a valid access token
                // If status is OK, we know the user is logged in
                if (session.status === Status.OK) {
                    // user is already logged in, so route to dashboard
                    router.push('/')
                } else {
                    // user is not logged in, so call handle login
                    handleLogin(sessionClient)
                }
                break
            // no default
        }
    }, [router, sessionClient, session.status])

    if (session.status === Status.OK) {
        return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
    }

    if (session.status === Status.LOADING || session.status === Status.INIT) {
        return <AuthContext.Provider value={authContext}>
            <Box component="main" sx={AuthStyles.loadingWrapper}>
                <C1CircularProgress />
                <CdnWarning />
            </Box>
        </AuthContext.Provider>
    }

    let err: RpcError | null = session.error
    if (err?.message === 'ErrorNoAuthCode' || err?.message === 'ErrorInvalidToken' || err?.message === 'ErrorInvalidGrant') {
        err = null
    }

    return <AuthContext.Provider value={authContext}>
        <LoginLayout>
            <LoginCard>
                <LoginTitle>
                    <FormattedMessage id="component_auth_login_title" defaultMessage="Welcome back" />
                </LoginTitle>
                {err && <Box mt={4}>
                    <APIError
                        err={err}
                        title={
                            <FormattedMessage
                                id="component_auth_internal_error"
                                defaultMessage="Error during authentication. Please contact your IT administrator."
                            />
                        }
                        variant="banner"
                    />
                </Box>}
                <LoginButton
                    onClick={(): void => {
                        setButtonDisabled(true)
                        handleLogin(sessionClient)
                    }}
                    disabled={buttonDisabled}
                >
                    <Typography>
                        <FormattedMessage
                            id="component_auth_login_button"
                            defaultMessage="Sign in to {tenant}.{installation}"
                            values={{
                                tenant: <TenantDomain />,
                                installation: <InstallationDomain />,
                            }}
                        />
                    </Typography>
                </LoginButton>
                <Box sx={{ textAlign: 'center' }}>
                    <PrivacyAndTerms />
                </Box>
            </LoginCard>
        </LoginLayout>
    </AuthContext.Provider>
}

export default AuthContextProvider
